import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

export interface DuplicateAnalysisTemplateModalProps {
  sourceAnalysisTemplateName: string;
  sourceAnalysisTemplateCollectionId: string;
  sourceTemplateIsPublished: boolean;
  actionOrigin?: ActionOrigin;
  onRequestClose: () => void;
  onRequestSubmit: (params: {name: string; templateCollectionId: string}) => Promise<void>;
}

export default function DuplicateAnalysisTemplateModal(
  props: DuplicateAnalysisTemplateModalProps
): JSX.Element {
  const [name, setName] = useState(`Copy of ${props.sourceAnalysisTemplateName}`);
  const [templateCollectionId, setTemplateCollectionId] = useState<string>(
    props.sourceAnalysisTemplateCollectionId
  );

  const trimmedName = name.trim();
  const isNameBlank = name == null || name.trim() === '';
  const isNameInvalid =
    trimmedName === props.sourceAnalysisTemplateName &&
    templateCollectionId === props.sourceAnalysisTemplateCollectionId;

  function isSubmitDisabled(): boolean {
    return isNameInvalid || isNameBlank;
  }

  return (
    <Modal
      title="Duplicate template"
      buttons={[
        {
          text: 'Duplicate',
          submit: true,
          disabled: isSubmitDisabled(),
          tooltipTitle: isNameBlank
            ? `Template name must be non-empty.`
            : isNameInvalid
              ? `Template name ${trimmedName} is already in use in this template collection. Use a different name or select a different template collection.`
              : null,
        },
      ]}
      onRequestSubmit={async () => {
        await props.onRequestSubmit({
          name,
          templateCollectionId,
        });
      }}
      onRequestClose={() => {
        props.onRequestClose();
      }}
      analyticsModule={ANALYTICS_MODULE.ANALYSIS_TOOL}
      analyticsModalType="duplicate_analysis_template"
      analyticsMetadata={{
        actionOrigin: props.actionOrigin,
        sourceTemplateStatus: props.sourceTemplateIsPublished ? 'published' : 'draft',
      }}
      dataTestAttrs={{component: 'DuplicateAnalysisTemplateModal', element: 'root'}}
    >
      <FormGroup>
        <Row>
          <Col md={6}>
            <Label>Name</Label>
            <TextInput
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              autoFocus={true}
              {...getTestProps({component: 'DuplicateAnalysisTemplateModal', element: 'nameInput'})}
            />
          </Col>
          <Col md={6}>
            <Label>Template collection</Label>
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(collection) => {
                setTemplateCollectionId(collection.id);
              }}
            />
          </Col>
        </Row>
      </FormGroup>
    </Modal>
  );
}
